/**
 * Canonical programme brand colours, keyed by `bridge.programme_definitions.code`.
 *
 * These are each programme's own brand hue (sampled from its logo), not derived
 * from the app palette in colors.ts. The programme's logo image
 * (`logo_letter_url`) is the canonical mark; this colour is the accent shown
 * alongside it (heading rules, icon tints, pills). Kept as raw hex so any app
 * can apply it inline, independent of Tailwind's class scanner.
 *
 * Single source of truth: Bridge and the accounts document surfaces both read
 * from here so a programme's colour stays consistent everywhere.
 */
export const programmeColors = {
  ers: "#1994fc",
  vantage: "#ec6823",
  thrive: "#76bc43",
  resound: "#1e94b8",
} as const;

export type ProgrammeCode = keyof typeof programmeColors;

/** Neutral accent for an unknown or unbranded programme code (neutral-400). */
export const PROGRAMME_COLOR_FALLBACK = "#9ca3af";

/**
 * Resolve a programme's brand colour by code, falling back to a neutral accent
 * for any code we don't have a brand colour for.
 */
export function getProgrammeColor(code: string | null | undefined): string {
  if (!code) {
    return PROGRAMME_COLOR_FALLBACK;
  }
  return programmeColors[code as ProgrammeCode] ?? PROGRAMME_COLOR_FALLBACK;
}
